import { useAutoneTranslation } from '@autone/translations';
import { format } from 'date-fns';
import { CalendarIcon } from 'lucide-react';
import { type ComponentProps, forwardRef, useCallback, useMemo } from 'react';
import { NumericFormat, type NumericFormatProps } from 'react-number-format';

import {
  DatePickerCalendar,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../atoms';
import {
  formatDatePickerDate,
  type SingleDatePickerProps,
} from '../../../../atoms/DatePicker';
import { type InputProps } from '../../../../atoms/inputs/Input';
import { cn } from '../../../../utils';

/**
 * A default text input for string values.
 */
const DefaultStringInput = forwardRef<HTMLInputElement, InputProps>(
  ({ ...props }, ref) => {
    const { common } = useAutoneTranslation();

    return (
      <input
        type="text"
        className={cn('h-full text-sm px-3 py-1 outline-none')}
        placeholder={common('advanced-filters.placeholders.enter-a-value')}
        data-testid="advanced-filters-string-input"
        {...props}
        ref={ref}
      />
    );
  },
);

DefaultStringInput.displayName = 'AdvancedFilterDefaultStringInput';

/**
 * A default number input for numeric values.
 */
const DefaultNumberInput = forwardRef<HTMLInputElement, NumericFormatProps>(
  ({ value, onValueChange, ...props }, ref) => {
    const { common } = useAutoneTranslation();

    return (
      <NumericFormat
        className={'h-full outline-none px-3 py-1 text-sm'}
        placeholder={common('advanced-filters.placeholders.enter-a-value')}
        data-testid="advanced-filters-number-input"
        value={value}
        onValueChange={onValueChange}
        {...props}
        getInputRef={ref}
      />
    );
  },
);

DefaultNumberInput.displayName = 'AdvancedFilterDefaultNumberInput';

interface DefaultSelectInputProps<T extends string = string>
  extends Omit<ComponentProps<typeof Select>, 'value' | 'onValueChange'> {
  value?: T;
  onValueChange: (value: T) => void;
  options: { id: T; label: string }[];
}

/**
 * A default input for single-select values.
 */
function DefaultSingleSelectInput<T extends string = string>({
  value,
  onValueChange,
  options,
  ...props
}: DefaultSelectInputProps<T>) {
  const { common } = useAutoneTranslation();

  return (
    <Select value={value} onValueChange={onValueChange} {...props}>
      <SelectTrigger
        id="advanced-filters-trigger"
        aria-label="Advanced filters trigger"
        className={cn(
          'focus-within:ring-0 focus-visible:outline-none focus-within:outline-none focus-visible:ring-0',
          'w-full h-8 rounded-none border-none',
        )}
      >
        <div className="w-full text-start overflow-hidden text-ellipsis text-sm">
          <SelectValue
            placeholder={common('advanced-filters.placeholders.select-a-value')}
          />
        </div>
      </SelectTrigger>
      <SelectContent data-testid="advanced-filters-single-select-content">
        <SelectGroup>
          {options.map(({ id, label }) => (
            <SelectItem key={id} value={id} className="text-sm">
              {label}
            </SelectItem>
          ))}
        </SelectGroup>
      </SelectContent>
    </Select>
  );
}

interface DefaultSingleDatePickerProps
  extends Omit<SingleDatePickerProps, 'date' | 'onChange' | 'placeholderText'> {
  /**
   * A date string in 'yyyy-MM-dd' format.
   */
  value?: string;
  /**
   * Callback when the date value changes. The value is a date string in 'yyyy-MM-dd' format.
   */
  onValueChange: (value?: string) => void;
  placeholderText?: string;
}

/**
 * A default single date picker input for single date values. Uses date strings in 'yyyy-MM-dd'
 * format.
 */
const DefaultSingleDatePicker = forwardRef<
  HTMLButtonElement,
  DefaultSingleDatePickerProps
>(({ value, onValueChange, ...props }, ref) => {
  const { common, lang } = useAutoneTranslation();

  const valAsDate = useMemo(
    () => (value ? new Date(value) : undefined),
    [value],
  );

  const handleChange = useCallback(
    (date: Date) => {
      onValueChange(format(date, 'yyyy-MM-dd'));
    },
    [onValueChange],
  );

  return (
    <Popover {...props}>
      <PopoverTrigger
        className="h-full px-3 py-1 flex flex-row text-sm items-center justify-between"
        ref={ref}
      >
        <p>
          {valAsDate
            ? formatDatePickerDate(valAsDate, lang)
            : common('pick-a-date')}
        </p>
        <CalendarIcon size={14} />
      </PopoverTrigger>
      <PopoverContent
        data-testid="advanced-filters-single-date-picker-content"
        className="w-auto p-0"
        align="start"
      >
        <DatePickerCalendar
          required={true}
          mode="single"
          selected={valAsDate}
          onSelect={handleChange}
          showOutsideDays={true}
        />
      </PopoverContent>
    </Popover>
  );
});

DefaultSingleDatePicker.displayName = 'AdvancedFilterDefaultSingleDatePicker';

export const AdvancedFiltersDefaultInputs = {
  String: DefaultStringInput,
  Number: DefaultNumberInput,
  SingleDate: DefaultSingleDatePicker,
  SingleSelect: DefaultSingleSelectInput,
};
